
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Visual pagination footer used by the Cards view of every listing page.
 * Matches the Kendo Grid pager visually so switching between Cards and
 * Table feels consistent (same page-size dropdown, same range label).
 *
 * Props:
 *   total         total number of items across all pages
 *   skip          how many items are skipped (current page * pageSize)
 *   take          page size
 *   onPageChange  ({skip, take}) => void
 *   pageSizes     dropdown options (default [5, 10, 20])
 */
export const CardsPagination = ({
  total,
  skip,
  take,
  onPageChange,
  pageSizes = [5, 10, 20],
}) => {
  if (total === 0) return null;

  const currentPage = Math.floor(skip / take) + 1;
  const totalPages = Math.max(1, Math.ceil(total / take));
  const start = total === 0 ? 0 : skip + 1;
  const end = Math.min(skip + take, total);

  const goTo = (page) => {
    const clamped = Math.max(1, Math.min(totalPages, page));
    onPageChange({ skip: (clamped - 1) * take, take });
  };

  const changePageSize = (newTake) => {
    onPageChange({ skip: 0, take: newTake });
  };

  const btnStyle = (disabled) => ({
    width: 32, height: 32, borderRadius: 6,
    border: '1px solid #E8ECF0',
    background: disabled ? '#F8FAFB' : '#fff',
    color: disabled ? '#CBD5E0' : '#374151',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  });

    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', marginTop: 12,
      background: '#fff', borderRadius: 12, border: '1px solid #E8ECF0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      flexWrap: 'wrap', gap: 12,
    }}>
      {/* Page size selector */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6B7280' }}>
        <span>Page size</span>
        <select
          value={take}
          onChange={(e) => changePageSize(Number(e.target.value))}
          style={{
            padding: '5px 8px', borderRadius: 6,
            border: '1px solid #E8ECF0', background: '#fff',
            color: '#1F2937', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {pageSizes.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Page navigation */}
          <div
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
              
          >
                <button
                  onClick={() => goTo(1)}
                  disabled={currentPage === 1}
                  title="First page"
                  style={btnStyle(currentPage === 1)}
                >
                  <ChevronsLeft size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => goTo(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                  style={btnStyle(currentPage === 1)}
                >
                  <ChevronLeft size={14} strokeWidth={2.5} />
                </button>

                <div style={{
                  padding: '0 12px', minWidth: 110, textAlign: 'center',
                  fontSize: 13, fontWeight: 600, color: '#1F2937',
                }}>
                  Page <span style={{ color: '#0D9488' }}>{currentPage}</span> of {totalPages}
                </div>

                <button
                  onClick={() => goTo(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  title="Next page"
                  style={btnStyle(currentPage >= totalPages)}
                >
                  <ChevronRight size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => goTo(totalPages)}
                  disabled={currentPage >= totalPages}
                  title="Last page"
                  style={btnStyle(currentPage >= totalPages)}
                >
                  <ChevronsRight size={14} strokeWidth={2.5} />
                </button>
          </div>


          

      {/* Range label */}
      <div style={{ fontSize: 13, color: '#6B7280' }}>
        <span style={{ color: '#1F2937', fontWeight: 600 }}>{start}–{end}</span> of <span style={{ color: '#1F2937', fontWeight: 600 }}>{total}</span> items
      </div>
    </div>
  );
};

export default CardsPagination;
